/**
 * Feedback Service
 * Handles user feedback (thumbs up/down, comments) on bot messages.
 * Payload shape matches rate-loader exactly: userId, sessionId, messageId (= jobNumber),
 * agentName, agentResponse, userFeedback ('like'|'dislike'|''), userComment, createdAt,
 * accessLocation.
 */

import { submitFeedback as submitFeedbackAPI } from './apiService';
import { FEATURE_FLAGS } from '../constants';

export interface FeedbackPayload {
  userId: string;
  sessionId: string;
  messageId: string;          // jobNumber — NOT the chat message ID
  agentName: string;
  agentResponse: string;
  userFeedback: 'like' | 'dislike' | '';
  userComment?: string;
  createdAt: string;
  accessLocation?: string;    // 'core' | 'provider-console'
}

export interface FeedbackResult {
  success: boolean;
  error?: string;
}

/**
 * Submit user feedback for a bot message.
 * No-ops when feedback feature flag is disabled.
 */
export const sendFeedback = async (payload: FeedbackPayload): Promise<FeedbackResult> => {
  if (!FEATURE_FLAGS.ENABLE_FEEDBACK) {
    return { success: true }; // silently succeed
  }

  try {
    await submitFeedbackAPI(payload);
    return { success: true };
  } catch (error: any) {
    console.error('[FeedbackService] sendFeedback failed:', error);
    return { success: false, error: error.message || 'Failed to submit feedback' };
  }
};

/**
 * Build a FeedbackPayload from common chat message data.
 * Matches rate-loader's createFeedbackPayload shape exactly.
 */
export const buildFeedbackPayload = (
  message: { content?: string; metadata?: any },
  userId: string,
  sessionId: string,
  jobNumber: string,
  reaction: 'like' | 'dislike' | '',
  comment?: string,
  isCoreFlow?: boolean,
): FeedbackPayload => {
  const metadata = message.metadata || {};
  return {
    userId,
    sessionId,
    messageId: jobNumber,
    agentName: metadata.agentName || 'unknown',
    agentResponse: metadata.apiResponse
      ? (typeof metadata.apiResponse === 'string'
          ? metadata.apiResponse
          : JSON.stringify(metadata.apiResponse))
      : (message.content || ''),
    userFeedback: reaction,
    userComment: comment || '',
    createdAt: new Date().toISOString(),
    accessLocation: isCoreFlow ? 'core' : 'provider-console',
  };
};

export default {
  sendFeedback,
  buildFeedbackPayload,
};
